import { useEffect, useState, useRef } from "react";
import { PortalLinkVencido } from "../components/sistema/PaginaPortal.jsx";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { useAuth } from "../context/AuthContext.jsx";
import AuthShell, { BotonPrimario, BotonSecundario } from "../components/AuthShell.jsx";

/**
 * VerifyMagic · v8.5.63 · piel — 3 estados explícitos en la mitad derecha
 * de AuthShell (verificando/listo/vencido) en vez del redirect automático
 * de antes: el login exitoso ahora se detiene en "listo" con un botón
 * "Entrar" que el usuario dispara, no un salto instantáneo sin feedback.
 * "Vencido" cubre tanto el link realmente expirado como cualquier otro
 * error (token inválido, sin clientes asociados) — el backend distingue
 * "expirado" de "inválido o ya usado" solo por texto, sin código
 * estructurado, y el spec no define un tercer estado visual para eso.
 */
export default function VerifyMagic() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [estado, setEstado] = useState("verificando");
  const [error, setError] = useState(null);
  const [clientes, setClientes] = useState([]);
  const verifyingRef = useRef(false);

  useEffect(() => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;

    const token = params.get("token");
    if (!token) { setError("Token faltante en el link"); setEstado("vencido"); return; }

    (async () => {
      try {
        const r = await api.verifyToken(token);
        await loginWithToken(r.session_token, r.user, r.clientes);
        if (!r.clientes?.length) {
          setError("Tu email no tiene clientes asociados. Contactá a tu ejecutivo de cuenta.");
          setEstado("vencido");
          return;
        }
        setClientes(r.clientes);
        setEstado("listo");
      } catch (err) {
        setError(err.message);
        setEstado("vencido");
      }
    })();
  }, []);

  const entrar = () => navigate(clientes.length > 1 ? "/elegir-cliente" : "/", { replace: true });

  // Link vencido → página de sistema del portal (breve, sin códigos).
  if (estado === "vencido") return <PortalLinkVencido />;

  return (
    <AuthShell>
      {estado === "verificando" && (
        <p className="font-display text-[24px] leading-tight text-[var(--ink)] animate-pulse">Verificando tu link…</p>
      )}
      {estado === "listo" && (
        <>
          <h1 className="h1-portal text-[var(--done)]">Listo</h1>
          <p className="text-[15px] text-[var(--muted)] mt-2">Tu link es válido — ya podés entrar al portal.</p>
          <div className="mt-6"><BotonPrimario type="button" onClick={entrar}>Entrar</BotonPrimario></div>
        </>
      )}
      {estado === "vencido" && (
        <>
          <h1 className="h1-portal text-[var(--danger)]">Este link ya no sirve</h1>
          <p className="text-[15px] text-[var(--muted)] mt-2">{error}</p>
          <div className="mt-6"><BotonSecundario href="/login">Pedir uno nuevo</BotonSecundario></div>
        </>
      )}
    </AuthShell>
  );
}
